/**
 * Pricing Configuration
 * Defines subscription plans, pricing, and features for Plot Studio
 *
 * Note: Pricing data is synchronized with the database license_plans table.
 * The database is the source of truth for pricing.
 */

import { supabase } from '@/services/supabase/supabase';

export interface PlanFeature {
  name: string;
  included: boolean;
  description?: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: {
    monthly: number;
    yearly: number;
    currency: string;
  };
  features: PlanFeature[];
  popular?: boolean;
  buttonText: string;
  razorpayPlanId?: {
    monthly: string;
    yearly: string;
  };
}

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'trial',
    name: 'Trial',
    description: '7-day access to all features',
    price: {
      monthly: 0,
      yearly: 0,
      currency: 'INR'
    },
    features: [
      { name: 'Limited Projects (3)', included: true },
      { name: 'Basic Chapter Management', included: true },
      { name: 'AI Assistant (50 Credits)', included: true },
      { name: 'Export options', included: true }
    ],
    buttonText: 'Current Trial'
  },
  {
    id: 'core',
    name: 'Plot Studio Core',
    description: 'The Author\'s Core. Pure, distraction-free writing tools for the dedicated storyteller.',
    price: {
      monthly: 499,
      yearly: 4999,
      currency: 'INR'
    },
    features: [
      { name: 'Unlimited Projects', included: true },
      { name: 'Advanced Chapter Management', included: true },
      { name: 'Character & World Building', included: true },
      { name: 'Timeline & Plot Tools', included: true },
      { name: 'Distraction-Free Mode', included: true },
      { name: 'Advanced Export (PDF/ePub)', included: true },
      { name: 'No AI Features', included: false, description: 'Pure human creativity' },
      { name: 'Priority Email Support', included: true }
    ],
    buttonText: 'Choose Core',
    razorpayPlanId: {
      monthly: 'plan_R1icrYXYIGvy8E', // TODO: Update with real ID for Core
      yearly: 'plan_R1ieT6HVM040cF'   // TODO: Update with real ID for Core
    }
  },
  {
    id: 'plus',
    name: 'Plot Studio Plus',
    description: 'The Author\'s AI. Supercharge your creativity with an intelligent co-pilot and generation tools.',
    price: {
      monthly: 1499,
      yearly: 14999,
      currency: 'INR'
    },
    features: [
      { name: 'Everything in Core', included: true },
      { name: 'AI Writing Assistant', included: true },
      { name: 'AI Storytelling Agent', included: true },
      { name: 'Smart Plot Suggestions', included: true },
      { name: 'Character Portrait Generation', included: true },
      { name: 'Voice-to-Text Dictation', included: true },
      { name: 'Monthly AI Credits (500)', included: true },
      { name: 'Priority 24/7 Support', included: true }
    ],
    popular: true,
    buttonText: 'Choose Plus',
    razorpayPlanId: {
      monthly: 'plan_R1igbEMZK4Ecaz', // TODO: Update with real ID for Plus
      yearly: 'plan_R1ifmslAlWtZ5F'   // TODO: Update with real ID for Visionary
    }
  }
];



/**
 * Get available upgrade plans for a user based on their current plan
 */
export function getAvailableUpgrades(currentPlan: string): SubscriptionPlan[] {
  // Filter out Ultra and Trial plans from all operations
  const filteredPlans = SUBSCRIPTION_PLANS.filter(plan =>
    plan.id !== 'trial' && plan.id !== 'ultra' && plan.name.toLowerCase() !== 'ultra'
  );

  const currentPlanIndex = filteredPlans.findIndex(plan =>
    plan.name.toLowerCase() === currentPlan.toLowerCase()
  );

  // If user is on trial or plan not found, show all paid plans (excluding trial and ultra)
  if (currentPlanIndex === -1 || currentPlan.toLowerCase() === 'trial') {
    return filteredPlans;
  }

  // Return plans that are higher than current plan
  return filteredPlans.slice(currentPlanIndex + 1);
}

/**
 * Get plan by ID
 */
export function getPlanById(planId: string): SubscriptionPlan | undefined {
  return SUBSCRIPTION_PLANS.find(plan => plan.id === planId);
}

/**
 * Get plan by name
 */
export function getPlanByName(planName: string): SubscriptionPlan | undefined {
  return SUBSCRIPTION_PLANS.find(plan =>
    plan.name.toLowerCase() === planName.toLowerCase()
  );
}

/**
 * Format price for display
 */
export function formatPrice(amount: number, currency: string = 'INR'): string {
  if (amount === 0) return 'Free';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Calculate savings for yearly plans
 */
export function calculateYearlySavings(plan: SubscriptionPlan): number {
  const monthlyTotal = plan.price.monthly * 12;
  const yearlyPrice = plan.price.yearly;
  return monthlyTotal - yearlyPrice;
}

/**
 * Fetch license plans from database
 * This ensures pricing is always up-to-date with the database
 */
export async function fetchLicensePlans(): Promise<SubscriptionPlan[]> {
  try {
    const { data, error } = await supabase.rpc('get_license_plans');

    if (error) {
      console.error('Error fetching license plans:', error);
      return SUBSCRIPTION_PLANS; // Fallback to static plans
    }

    if (!data || data.length === 0) {
      return SUBSCRIPTION_PLANS; // Fallback to static plans
    }

    // Convert database plans to SubscriptionPlan format, filtering out Ultra plan
    // Map 'Base' -> 'Storyteller' and 'Pro' -> 'Visionary'
    return data
      .filter((dbPlan: any) => dbPlan.name.toLowerCase() !== 'ultra') // Filter out Ultra plan
      .map((dbPlan: any) => {
        let mappedName = dbPlan.name;
        if (dbPlan.name === 'Base') mappedName = 'Plot Studio Core';
        if (dbPlan.name === 'Pro') mappedName = 'Plot Studio Plus';

        const staticPlan = SUBSCRIPTION_PLANS.find(p => p.name.toLowerCase() === mappedName.toLowerCase());

        if (!staticPlan) {
          // If it's Trial, return the static Trial plan
          if (dbPlan.name === 'Trial') {
            const trialPlan = SUBSCRIPTION_PLANS.find(p => p.id === 'trial');
            return trialPlan || SUBSCRIPTION_PLANS[0];
          }
          // Fallback for unknown plans, construct a basic object
          return {
            id: dbPlan.name.toLowerCase(),
            name: dbPlan.name,
            description: `${dbPlan.name} plan features`,
            price: {
              monthly: Number(dbPlan.monthly_price),
              yearly: Number(dbPlan.yearly_price),
              currency: dbPlan.currency
            },
            features: [],
            buttonText: `Upgrade to ${dbPlan.name}`,
          } as SubscriptionPlan;
        }

        return {
          id: staticPlan.id,
          name: staticPlan.name,
          description: staticPlan.description, // Use static description for better copy
          price: {
            monthly: Number(dbPlan.monthly_price),
            yearly: Number(dbPlan.yearly_price),
            currency: dbPlan.currency
          },
          features: staticPlan.features,
          popular: staticPlan.popular,
          buttonText: staticPlan.buttonText,
          razorpayPlanId: staticPlan.razorpayPlanId
        };
      });
  } catch (error) {
    console.error('Error fetching license plans:', error);
    return SUBSCRIPTION_PLANS; // Fallback to static plans
  }
}